import type { RicsSelections } from '@revelio/iso-utility';

import type { TreeFilter } from '../types/filters';

/**
 * Extracts the RICS id groups the adaptive-taxonomy computation reads from the
 * merged company+industry filter (shared by the role/skill/activity adaptive
 * hooks).
 *
 * `rics_k400` industry selections are included so a k400 industry resolves to
 * its k50 parent via `ricsK400List` (the dashboard exposes k10 and k400
 * industry levels, so k400 must roll up to k50 rather than fall through to a
 * k10 taxonomy).
 */
export const extractRicsSelections = (
  companyFilter: TreeFilter
): RicsSelections => {
  const rics_k400 = companyFilter.selections
    .filter(
      ({ levelId }) =>
        levelId === 'company' ||
        levelId === 'rics_k50' ||
        levelId === 'rics_k400'
    )
    .map((selection) => selection.id.split('_')?.[1] ?? null)
    .filter((d): d is string => d !== null)
    .filter((d) => Number(d) <= 400);

  const rics_k10 = companyFilter.selections
    .map((selection) => selection.id.split('_')?.[0] ?? null)
    .filter((d): d is string => d !== null)
    .filter((d) => Number(d) <= 10);

  const rics_k50 = companyFilter.selections
    .filter(({ levelId }) => levelId === 'rics_k50')
    .map((selection) => selection.id.split('_')?.[1] ?? selection.id)
    .filter((d): d is string => d !== null)
    .filter((d) => Number(d) <= 50);

  return { rics_k10, rics_k50, rics_k400 };
};
